import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { addToast, Button } from '@structural-climbing/ui'

/** How long the button stays green after a copy. */
export const COPIED_FEEDBACK_MS = 2000

interface CopyLinkButtonProps {
  label: string
  url: string
}

/**
 * Copies a link and says so on the button itself: it turns green with a check
 * mark for a moment, so the confirmation is where the eye already is.
 */
export function CopyLinkButton({ label, url }: CopyLinkButtonProps) {
  const { t } = useTranslation()
  const [copied, setCopied] = useState(false)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current)
    },
    [],
  )

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(url)
    } catch {
      addToast({ title: t('requests.errorCopyLink'), color: 'danger' })
      return
    }
    setCopied(true)
    if (timer.current) clearTimeout(timer.current)
    timer.current = setTimeout(() => setCopied(false), COPIED_FEEDBACK_MS)
  }

  return (
    <Button
      size="sm"
      variant="flat"
      color={copied ? 'success' : 'default'}
      className="transition-colors duration-300"
      onClick={() => void handleCopy()}
      startContent={
        // Keyed, so the icon remounts and the pop plays on every swap.
        <svg
          key={copied ? 'copied' : 'link'}
          className="h-4 w-4 shrink-0 animate-appearance-in"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d={
              copied
                ? 'M5 13l4 4L19 7'
                : 'M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1'
            }
          />
        </svg>
      }
    >
      <span aria-live="polite">{copied ? t('requests.linkCopied') : label}</span>
    </Button>
  )
}
